// =============================================================================
// PTEL - Audit Service
// =============================================================================
// Registra todas las acciones en Firestore para auditoría completa
// =============================================================================

import type {
  QueryConstraint,
  QueryDocumentSnapshot,
  Unsubscribe} from 'firebase/firestore';
import {
  collection,
  addDoc,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  onSnapshot,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db, isConfigured, COLLECTIONS } from '../../config/firebase';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

export type AuditAction = 'create' | 'update' | 'delete' | 'approve' | 'reject' | 'login' | 'logout' | 'expire';
export type AuditEntityType = 'infrastructure' | 'pattern' | 'document' | 'session';

export interface AuditEntry {
  id: string;
  action: AuditAction;
  entityType: AuditEntityType;
  entityId: string;
  userId: string;
  userEmail: string;
  municipalityINE: string;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  timestamp: Date;
  ipAddress: string | null;
}

interface CreateAuditEntryParams {
  action: AuditAction;
  entityType: AuditEntityType;
  entityId: string;
  userId: string;
  userEmail: string;
  municipalityINE: string;
  before?: Record<string, unknown> | null;
  after?: Record<string, unknown> | null;
}


// -----------------------------------------------------------------------------
// Create Audit Entry
// -----------------------------------------------------------------------------

export async function createAuditEntry(params: CreateAuditEntryParams): Promise<string> {
  if (!isConfigured || !db) {
    console.warn('Firebase not configured, skipping audit');
    return '';
  }

  try {
    const docRef = await addDoc(collection(db, COLLECTIONS.AUDIT_LOG), {
      action: params.action,
      entityType: params.entityType,
      entityId: params.entityId,
      userId: params.userId,
      userEmail: params.userEmail,
      municipalityINE: params.municipalityINE,
      before: params.before || null,
      after: params.after || null,
      timestamp: serverTimestamp(),
      ipAddress: null,
    });
    return docRef.id;
  } catch (error) {
    console.error('Error creating audit entry:', error);
    return '';
  }
}

// -----------------------------------------------------------------------------
// Convenience Functions
// -----------------------------------------------------------------------------

export async function logPatternAction(
  action: AuditAction,
  patternId: string,
  userId: string,
  userEmail: string,
  municipalityINE: string,
  before?: Record<string, unknown> | null,
  after?: Record<string, unknown> | null
): Promise<string> {
  return createAuditEntry({
    action,
    entityType: 'pattern',
    entityId: patternId,
    userId,
    userEmail,
    municipalityINE,
    before,
    after,
  });
}

export async function logInfrastructureAction(
  action: AuditAction,
  infraId: string,
  userId: string,
  userEmail: string,
  municipalityINE: string,
  before?: Record<string, unknown> | null,
  after?: Record<string, unknown> | null
): Promise<string> {
  return createAuditEntry({
    action,
    entityType: 'infrastructure',
    entityId: infraId,
    userId,
    userEmail,
    municipalityINE,
    before,
    after,
  });
}

export async function logDocumentAction(
  action: AuditAction,
  documentId: string,
  userId: string,
  userEmail: string,
  municipalityINE: string,
  before?: Record<string, unknown> | null,
  after?: Record<string, unknown> | null
): Promise<string> {
  return createAuditEntry({
    action,
    entityType: 'document',
    entityId: documentId,
    userId,
    userEmail,
    municipalityINE,
    before,
    after,
  });
}

export async function logSessionAction(
  action: 'login' | 'logout',
  userId: string,
  userEmail: string,
): Promise<string> {
  return createAuditEntry({
    action,
    entityType: 'session',
    entityId: `session-${userId}`,
    userId,
    userEmail,
    municipalityINE: '',
  });
}

export async function logRoleExpiration(
  userId: string,
  userEmail: string,
  previousRole: string,
): Promise<string> {
  return createAuditEntry({
    action: 'expire',
    entityType: 'session',
    entityId: userId,
    userId,
    userEmail,
    municipalityINE: '',
    before: { role: previousRole },
    after: { role: 'viewer' },
  });
}


// -----------------------------------------------------------------------------
// Query Audit Log
// -----------------------------------------------------------------------------

export interface QueryAuditParams {
  entityType?: AuditEntityType;
  entityId?: string;
  userId?: string;
  municipalityINE?: string;
  action?: AuditAction;
  startDate?: Date;
  endDate?: Date;
  maxResults?: number;
}

function buildAuditConstraints(params: QueryAuditParams): QueryConstraint[] {
  const constraints: QueryConstraint[] = [];

  if (params.entityType) {
    constraints.push(where('entityType', '==', params.entityType));
  }
  if (params.entityId) {
    constraints.push(where('entityId', '==', params.entityId));
  }
  if (params.userId) {
    constraints.push(where('userId', '==', params.userId));
  }
  if (params.municipalityINE) {
    constraints.push(where('municipalityINE', '==', params.municipalityINE));
  }
  if (params.action) {
    constraints.push(where('action', '==', params.action));
  }
  if (params.startDate) {
    constraints.push(where('timestamp', '>=', Timestamp.fromDate(params.startDate)));
  }
  if (params.endDate) {
    constraints.push(where('timestamp', '<=', Timestamp.fromDate(params.endDate)));
  }

  constraints.push(orderBy('timestamp', 'desc'));
  constraints.push(limit(params.maxResults || 100));
  return constraints;
}

function toAuditEntry(doc: QueryDocumentSnapshot): AuditEntry {
  const data = doc.data();
  return {
    id: doc.id,
    action: data.action as AuditAction,
    entityType: data.entityType as AuditEntityType,
    entityId: data.entityId,
    userId: data.userId,
    userEmail: data.userEmail,
    municipalityINE: data.municipalityINE,
    before: data.before,
    after: data.after,
    timestamp: data.timestamp?.toDate() || new Date(),
    ipAddress: data.ipAddress,
  };
}

/**
 * PANADM-b2 DOP-5 (a): un fallo de lectura se lanza; ya no se convierte en
 * «no hay entradas». Sin Firebase configurado, la lista vacía es legítima.
 */
export async function queryAuditLog(params: QueryAuditParams = {}): Promise<AuditEntry[]> {
  if (!isConfigured || !db) return [];

  const q = query(collection(db, COLLECTIONS.AUDIT_LOG), ...buildAuditConstraints(params));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(toAuditEntry);
}

/**
 * PANADM-b2 DOP-2 (b) y DOP-5 (a): el registro escucha en vivo. Las entradas
 * llegan por `onEntries` cada vez que cambian; un fallo llega por `onError`
 * (Firestore corta la escucha tras un error: para reintentar hay que volver a
 * llamar). Devuelve la función que deja de escuchar. Los índices son los mismos
 * que los de `queryAuditLog` (AUDIT_PANADMB2 §2.4).
 */
export function watchAuditLog(
  params: QueryAuditParams,
  onEntries: (entries: AuditEntry[]) => void,
  onError: (error: unknown) => void,
): Unsubscribe {
  if (!isConfigured || !db) {
    onEntries([]);
    return () => {};
  }

  const q = query(collection(db, COLLECTIONS.AUDIT_LOG), ...buildAuditConstraints(params));
  return onSnapshot(
    q,
    (snapshot) => onEntries(snapshot.docs.map(toAuditEntry)),
    (error) => onError(error),
  );
}

// -----------------------------------------------------------------------------
// Utility Functions
// -----------------------------------------------------------------------------

export function formatAuditDescription(entry: AuditEntry): string {
  const actionLabels: Record<AuditAction, string> = {
    create: 'creó',
    update: 'actualizó',
    delete: 'eliminó',
    approve: 'aprobó',
    reject: 'rechazó',
    login: 'inició sesión',
    logout: 'cerró sesión',
    expire: 'expiró rol de',
  };

  const entityLabels: Record<AuditEntityType, string> = {
    infrastructure: 'infraestructura',
    pattern: 'patrón',
    document: 'documento',
    session: '',
  };

  const action = actionLabels[entry.action] || entry.action;
  const entity = entityLabels[entry.entityType] || entry.entityType;

  if (entry.entityType === 'session') {
    return `${entry.userEmail} ${action}`;
  }
  return `${entry.userEmail} ${action} ${entity}`;
}

export function getChangedFields(
  before: Record<string, unknown> | null,
  after: Record<string, unknown> | null
): string[] {
  if (!before || !after) return [];

  const changedFields: string[] = [];
  const allKeys = new Set([...Object.keys(before), ...Object.keys(after)]);

  for (const key of allKeys) {
    if (JSON.stringify(before[key]) !== JSON.stringify(after[key])) {
      changedFields.push(key);
    }
  }

  return changedFields;
}
